import { useState, type ReactNode } from 'react';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import {
  emptyNetworkingDetail,
  formatNetworkingDetail,
  hasAnyNetworkingDetail,
  type NetworkingDetail,
} from '@/lib/conclave-networking';

type Props = {
  values: NetworkingDetail[];
  onChange: (values: NetworkingDetail[]) => void;
  itemLabel: string;
  heading?: string;
  tip?: string;
  addLabel?: string;
  maxItems?: number;
};

function firstEditableIndex(values: NetworkingDetail[]) {
  return values.findIndex((item) => !hasAnyNetworkingDetail(item));
}

function entryTitle(item: NetworkingDetail, itemLabel: string, index: number) {
  return item.name.trim() || item.businessName.trim() || `${itemLabel} ${index + 1}`;
}

function entrySummary(item: NetworkingDetail, title: string) {
  return formatNetworkingDetail(item)
    .split(' · ')
    .filter((part) => part !== title)
    .join(' · ');
}

export function DetailEntriesEditor({
  values,
  onChange,
  itemLabel,
  heading,
  tip,
  addLabel,
  maxItems,
}: Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(() => {
    const emptyIndex = firstEditableIndex(values);
    return emptyIndex >= 0 ? emptyIndex : null;
  });

  const setItem = <K extends keyof NetworkingDetail>(
    index: number,
    key: K,
    value: NetworkingDetail[K]
  ) => {
    onChange(values.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const filledCount = values.filter(hasAnyNetworkingDetail).length;
  const canAdd = editingIndex == null && (maxItems == null || filledCount < maxItems);

  const addAnother = () => {
    if (!canAdd) return;
    const emptyIndex = firstEditableIndex(values);
    if (emptyIndex >= 0) {
      setEditingIndex(emptyIndex);
      return;
    }
    onChange([...values, emptyNetworkingDetail()]);
    setEditingIndex(values.length);
  };

  return (
    <div className="space-y-4">
      {tip ? (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
          {tip}
        </div>
      ) : null}
      {heading ? <p className="text-sm font-semibold text-foreground">{heading}</p> : null}
      {values.map((item, index) => {
        const isEditing = editingIndex === index;
        if (!isEditing && hasAnyNetworkingDetail(item)) {
          const title = entryTitle(item, itemLabel, index);
          const summary = entrySummary(item, title);
          return (
            <div
              key={index}
              className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2.5 md:rounded-2xl md:px-4 md:py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">{title}</p>
                {summary ? <p className="mt-1 text-xs text-muted-foreground">{summary}</p> : null}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setEditingIndex(index)}>
                  Edit
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={() => {
                    const next = values.filter((_, i) => i !== index);
                    onChange(next.length > 0 ? next : [emptyNetworkingDetail()]);
                    setEditingIndex(next.length > 0 ? firstEditableIndex(next) : 0);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          );
        }
        if (!isEditing) return null;

        return (
          <div
            key={index}
            className="grid gap-3 rounded-xl border border-border bg-card p-3 md:grid-cols-2 md:rounded-2xl md:p-4"
          >
            <div className="md:col-span-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {itemLabel} {index + 1}
              </p>
              {values.length > 1 || hasAnyNetworkingDetail(item) ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => {
                    const next = values.filter((_, i) => i !== index);
                    onChange(next.length > 0 ? next : [emptyNetworkingDetail()]);
                    setEditingIndex(next.length > 0 ? firstEditableIndex(next) : 0);
                  }}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <Field label="Name">
              <Input value={item.name} onChange={(e) => setItem(index, 'name', e.target.value)} />
            </Field>
            <Field label="Phone">
              <PhoneInput
                value={item.phone || undefined}
                onChange={(v) => setItem(index, 'phone', v ?? '')}
                placeholder="Enter phone number"
                defaultCountry="IN"
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={item.email}
                onChange={(e) => setItem(index, 'email', e.target.value)}
              />
            </Field>
            <Field label="Business name">
              <Input
                value={item.businessName}
                onChange={(e) => setItem(index, 'businessName', e.target.value)}
              />
            </Field>
            <Field label="Category" className="md:col-span-2">
              <BusinessCategoryPicker
                businessCategory={item.businessCategory}
                subCategory={item.subCategory}
                placeholder="Search category…"
                onSelect={(value) => {
                  onChange(
                    values.map((row, i) =>
                      i === index
                        ? {
                            ...row,
                            businessCategory: value?.businessCategory ?? '',
                            subCategory: value?.subCategory ?? '',
                          }
                        : row
                    )
                  );
                }}
              />
            </Field>
            <Field label="Notes" className="md:col-span-2">
              <Textarea
                value={item.notes}
                onChange={(e) => setItem(index, 'notes', e.target.value)}
                className="min-h-24"
                placeholder="Any extra details…"
              />
            </Field>
            <div className="md:col-span-2 flex justify-end">
              <Button
                type="button"
                size="sm"
                className="rounded-xl"
                onClick={() => {
                  if (!hasAnyNetworkingDetail(item) && values.length > 1) {
                    onChange(values.filter((_, i) => i !== index));
                  }
                  setEditingIndex(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        );
      })}
      {canAdd ? (
        <Button type="button" variant="outline" className="w-full rounded-xl" onClick={addAnother}>
          {addLabel ?? `+ Add another ${itemLabel.toLowerCase()}`}
        </Button>
      ) : null}
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
    </label>
  );
}
